import { dashboardApiV3 } from '#api';
import { Dispatch, FC, SetStateAction, useEffect, useRef } from 'react';
import { useAppContext, useSafeState } from '#hooks';
import { DestinationV3, PlanScopeV3, PlanV3, PriceV3 } from '#types'
import { plansTableHeaders } from '#globals'
import { Pagination } from '#dashboard/components/pagination.component';

interface Filter {
    name: string;
    data: number;
    duration: number;
    provider: string;
}

interface PlansSearchBarProps {
    setSelectedPlan: Dispatch<SetStateAction<PlanV3>>;
    setShowConfirmation: Dispatch<SetStateAction<boolean>>;
    destinations: DestinationV3[];
    scope: PlanScopeV3;
    setScope: Dispatch<SetStateAction<PlanScopeV3>>;
}

export const PlansTableV3: FC<PlansSearchBarProps> = ({ setSelectedPlan, setShowConfirmation, destinations, scope, setScope }) => {

    const { setApiStatus, currentEnvironment } = useAppContext();
    const [isLoading, setIsLoading] = useSafeState<boolean>(true)
    const [iso3, setIso3] = useSafeState<string>('GBR');
    const [plans, setPlans] = useSafeState<PlanV3[]>([])
    const planScopes = Object.values(PlanScopeV3);
    const emptyFilter: Filter = { name: '', data: 0, duration: 0, provider: '' }
    const [filter, setFilter] = useSafeState<Filter>(emptyFilter)
    const [currentPage, setCurrentPage] = useSafeState<number>(1);
    const totalPagesRef = useRef<number>(1);
    const pageSize = 20

    const fetchPlans = async () => {
        setIsLoading(true)
        try {
            setCurrentPage(1)
            const response = await dashboardApiV3.getPlans(iso3, scope)
            setPlans([...response.data]);
        } catch (error: any) {
            setApiStatus({ code: error?.response?.data?.statusCode, message: error?.response?.data?.message })
        } finally {
            setIsLoading(false)
        }
    };

    const handlePlanClick = (plan: PlanV3) => {
        setSelectedPlan(plan)
        setShowConfirmation(true)
    }

    const findGBPPrice = (prices: PriceV3[]): string => {
        const gbp = prices.find(p => p.currencyISO === 'gbp');
        if (!gbp) return 'invalid price';

        const amount = gbp.value / 10 ** gbp.minorUnitsExponent;
        return `£${amount.toFixed(2)}`;
    };

    useEffect(
        () => {
            fetchPlans();
        },
        [currentEnvironment, iso3, scope]
    );


    if (isLoading) {
        return <div className="lds-hourglass" />
    }

    const filteredPlans = plans.filter(plan => {
        const matchesName = !filter.name || plan.name.toLowerCase().includes(filter.name.toLowerCase());
        const matchesData = !filter.data || (plan.dataUnit.toUpperCase() === 'GB' && plan.dataValue === filter.data);
        const matchesDuration = !filter.duration || (plan.durationUnit.toLowerCase() === 'days' && plan.durationValue === filter.duration);
        const matchesProvider = !filter.provider || plan.provider?.name.toLowerCase().includes(filter.provider.toLowerCase());
        return matchesName && matchesData && matchesDuration && matchesProvider;
    });

    totalPagesRef.current = Math.ceil(filteredPlans.length / pageSize) || 1;

    const paginatedPlans = filteredPlans.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    return <>
        <div className='pd-m main-dashboard-container mt-s'>
            <div className='three-columns-expand-three mb-l'>
                {
                    <div className='destinations-select-container mr-m'>
                        <p>Plan Scope: </p>
                        <select
                            onChange={(event) => setScope(event.target.value as PlanScopeV3)}
                            className='destinations-ddl'
                            value={scope}
                        >
                            {
                                planScopes?.map(
                                    (scopeOption) => <option
                                        className='destinations-options'
                                        key={scopeOption}
                                        value={scopeOption}>
                                        {scopeOption}
                                    </option>
                                )
                            }
                        </select>
                    </div>
                }
                {
                    scope === PlanScopeV3.LOCAL ? <div className='destinations-select-container'>
                        <p>Destination Name: </p>
                        <select
                            onChange={(event) => setIso3(event.target.value)}
                            className='destinations-ddl'
                            value={iso3}
                        >
                            {
                                destinations?.map(
                                    (destination) => <option
                                        className='destinations-options'
                                        key={destination.isoAlpha3}
                                        value={destination.isoAlpha3}>
                                        {destination.name}
                                    </option>
                                )
                            }
                        </select>
                    </div> : <div />
                }
                <div />
            </div>
            <div className='six-columns-expand-five mb-l'>
                <div className='mr-m two-columns-auto'>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <p>Name: </p>
                        <div />
                    </div>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <input
                            type="text"
                            value={filter.name || ''}
                            className='pl-m promo-input'
                            onChange={(e) => {
                                setFilter({ ...filter, name: e.target.value })
                                setCurrentPage(1)
                            }}
                        />
                        <div />
                    </div>
                </div>
                <div className='mr-m two-columns-auto'>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <p>Data (GB):</p>
                        <div />
                    </div>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <input
                            type="number"
                            value={filter.data || ''}
                            className='pl-m promo-input'
                            onChange={(e) => {
                                setFilter({ ...filter, data: Number(e.target.value) || 0 })
                                setCurrentPage(1)
                            }}
                        />
                        <div />
                    </div>
                </div>
                <div className='mr-m two-columns-auto'>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <p>Duration (Days):</p>
                        <div />
                    </div>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <input
                            type="number"
                            value={filter.duration || ''}
                            className='pl-m promo-input'
                            onChange={(e) => {
                                setFilter({ ...filter, duration: Number(e.target.value) || 0 })
                                setCurrentPage(1)
                            }}
                        />
                        <div />
                    </div>
                </div>
                <div className='mr-m two-columns-auto'>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <p>Provider: </p>
                        <div />
                    </div>
                    <div className='mr-m three-rows-expand-one-three'>
                        <div />
                        <input
                            type="text"
                            value={filter.provider || ''}
                            className='pl-m promo-input'
                            onChange={(e) => {
                                setFilter({ ...filter, provider: e.target.value })
                                setCurrentPage(1)
                            }}
                        />
                        <div />
                    </div>
                </div>
                <div />
                <button id='clear-filter-button' className='mr-m ml-m pd-s bold' onClick={() => setFilter(emptyFilter)}>CLEAR</button>
            </div>
            <table className='center-wrapper table'>
                <thead className="table-header">
                    <tr>
                        {
                            plansTableHeaders.map(
                                (header: string) => <th className="table-cell" key={header}>{header.toUpperCase()}</th>
                            )
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        paginatedPlans.map(
                            (plan: PlanV3, index: number) => (
                                <tr key={`${plan.nanoID}_${String(index)}`} className={'table-row'} onClick={() => handlePlanClick(plan)} >
                                    <td className="table-cell">{plan.nanoID}</td>
                                    <td className="table-cell">{plan.name}</td>
                                    <td className="table-cell">{plan.provider?.name}</td>
                                    <td className="table-cell">{plan.dataValue} {plan.dataUnit}</td>
                                    <td className="table-cell">{plan.durationValue} {plan.durationUnit}</td>
                                    <td className="table-cell">{findGBPPrice(plan.prices)}</td>
                                </tr>
                            )
                        )
                    }
                </tbody>
            </table>
            <Pagination totalPages={totalPagesRef.current} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </div>
    </>
}
